(ns practitest.frontend.requirements.index
  (:require [practitest.frontend.common-entities :refer [batch-navigate!
                                                         batch-open-in-new-tab!
                                                         common-index-grid-props
                                                         grid-columns] :as common-entities]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common-entities.index :refer [index-content-panel] :as common-index]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.find-and-replace-modal.events :as-alias find-and-replace-events]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [format-translation
                                                     get-translation] :as i18n]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common.re-frame :refer [partial-dispatch
                                                         partial-subscribe]]
            [practitest.frontend.components.grid.events :as grid.events]
            [practitest.frontend.components.grid.subs :as grid.subs]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components2.buttons :refer [button]]
            [practitest.frontend.forms3.entity-form.forms :as forms]
            [practitest.frontend.components2.grid :as grid2]
            [practitest.frontend.components2.grid.fields :refer [transform-fields]]
            [practitest.frontend.components2.grid.row-menu :refer [row-menu]]
            [practitest.frontend.events :as events]
            [practitest.frontend.import-ticket-modal.events :as import-ticket-events]
            [practitest.frontend.import-ticket-modal.main :as import-ticket]
            [practitest.frontend.import-ticket-modal.subs :as import-ticket-subs]
            [practitest.frontend.requirements.events :as requirements-events]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]))

(def edit-permission "requirements_editor")
(def admin-permission "requirements_admin")

(def current-entity :requirement)
(def table-id (get-in metadata/entities-metadata [current-entity :table-id]))
(def state-key requirements-events/requirements-index-state-key)


(def main-controller
  {:start    (fn []
               (re-frame/dispatch [::events/set-current-entity-type current-entity]))
   :stop     (fn []
               (when @(re-frame/subscribe [::import-ticket-subs/show-import-from-bugtracker-modal?])
                 (re-frame/dispatch [::import-ticket-events/toggle-import-from-bugtracker-modal])))
   :identity :parameters})

(defn dropdown-actions-menu [selection]
  (let [disabled? (not (permissions/has-permission? edit-permission))
        button-disabled? (grid.subs/empty-selection? selection)
        current-view @(re-frame/subscribe [::grid.subs/current-view current-entity state-key])
        track-fn #(mixpanel/track "Grid Actions" {"Action" %})
        filters (common-index/get-entity-filters current-entity)
        opts {:entity current-entity
              :state-key state-key
              :selection selection
              :current-view current-view
              :optional-params {:filter filters}}
        create-testset-fn (fn []
                            (track-fn "New Test Set")
                            (batch-navigate! (broutes/get-url-view "common#create_testset")
                                             selection current-view {:entity "Requirement"}))]
    [common-index/dropdown-actions-menu
     [{:text     (get-translation "entity.button.find_and_replace" "Find & Replace")
       :on-click (fn []
                   (let [n (if (= selection :_all)
                             @(re-frame/subscribe [::grid.subs/entities-total-count current-entity state-key])
                             (count selection))]
                     (when-not (common-entities/over-batch-limit n)
                       (re-frame/dispatch [::find-and-replace-events/open opts]))))
       :icon     [icons/find-and-replace]
       :disabled? button-disabled?}
      {:text     (get-translation "generic.button.delete" "Delete")
       :on-click #(common-events/batch-navigate-guarded
                   (assoc opts :path (broutes/get-url-view "requirements#batch_delete")))
       :icon    [icons/mux-trash]
       :disabled? button-disabled?}
      {:text     (get-translation "generic.button.print" "Print")
       :on-click (fn []
                   (track-fn "Print")
                   (batch-open-in-new-tab!
                      (broutes/get-url-view "requirements#print") selection current-view {:filter filters}))
       :icon     [icons/printer]
       :disabled? button-disabled?}
      {:disabled? (grid.subs/empty-selection? selection)
       :test-id "requirements-grid-create-testset"
       :on-click #(create-testset-fn)
       :text (get-translation "entity.button.create-testset" "New Test Set")
       :icon [icons/mux-testsets-runs]}]
     disabled?]))

(defn grid-buttons []
  (let [has-edit-permissions? (permissions/has-permission? edit-permission)
        selection @(re-frame/subscribe [::grid.subs/selection [current-entity state-key]])
        current-view @(re-frame/subscribe [::grid.subs/current-view current-entity state-key])
        bugtracker (:integrated_bugtracker @(re-frame/subscribe [::subs/current-project]))]
    [:div.entities-grid-toolbar.all-available-width
     [common-index/grid-buttons
      [(common-index/batch-edit-button-data current-entity selection current-view state-key has-edit-permissions?)]]
     (if bugtracker
       [common-index/new-entity-dropdown-btn current-entity]
       [button (common-index/new-entity-button-data current-entity)])
     (dropdown-actions-menu selection)]))

(re-frame/reg-sub
  ::filter-columns
  :<- [::subs/entity :requirement]
  (fn [entity [_ state-key]]
    (get-in entity [state-key :filter-cols])))

(def default-columns
  (get-in metadata/entities-metadata [current-entity :default-columns]))

(defmethod grid.subs/columns :requirement-columns [[_current-project entity-fields fields users groups folders] [_ [_entity _]]]
  (concat
    [{:type :url
      :field :name
      :default-width 200
      :autofilter? true
      :header "Name"}
     {:type :external_id_or_display_id
      :field :external_id
      :autofilter? true
      :header "External ID"}
     {:type          :status-bar
      :default-width 200
      :autofilter?   false
      :sortable?     false
      :header        "Requirement Status Bar"
      :field         :requirement_status_bar}
     {:type         :linked-entities
      :default-width 200
      :autofilter?   false
      :sortable?     false
      :header        "Linked Requirements"
      :field         :linked_requirements}
     {:type          :testing-scope
      :default-width 200
      :autofilter?   true
      :sortable?     true
      :header        "Testing Scope"
      :field         :testing_scope}
     {:type          :linked-entities
      :default-width 200
      :autofilter?   false
      :sortable?     false
      :header        "Linked Tests"
      :field         :linked_tests}
     {:type          :linked-entities
      :default-width 200
      :autofilter?   false
      :sortable?     false
      :header        "Linked Issues"
      :field         :linked_issues}]
    (transform-fields []
                      (dissoc (get entity-fields current-entity) :description)
                      fields users groups folders)))

(defn entities-grid [grid-state]
  (let [columns-id (get-in metadata/entities-metadata [current-entity :columns-id])
        all-columms (concat default-columns
                            (grid-columns @(re-frame/subscribe [::grid.subs/columns [current-entity columns-id]])
                                          @(re-frame/subscribe [::filter-columns state-key])))
        has-edit-permissions      (permissions/has-permission? edit-permission)
        has-admin-permissions      (permissions/has-permission? admin-permission)
        grid-props {:columns      (map #(assoc % :default-show? true) all-columms)
                    :post-columns [{:header        ""
                                    :field         :actions
                                    :default-width 50
                                    :width         50
                                    :type          :custom
                                    :text-align    :center
                                    :resizable?    false
                                    :sortable?     false
                                    :systemcol?    true
                                    :class         ["action-menu"]
                                    :cell-fn       (fn [_ row]
                                                     [row-menu row (not has-edit-permissions)
                                                      [{:label    (get-translation "generic.button.edit" "Edit")
                                                        :icon     icons/mux-pencil
                                                        :href     (:url row)
                                                        :disabled?    (not has-edit-permissions)
                                                        :on-click (fn [])}

                                                       {:label        (get-translation "generic.button.delete" "Delete")
                                                        :icon         icons/mux-trash
                                                        :disabled?    (not has-admin-permissions)
                                                        :on-click     (fn []
                                                                        (mixpanel/track "Grid Actions" {"Action" "Delete Requirement"})
                                                                        (re-frame/dispatch [::common-events/delete-entity (:id row) state-key grid-state]))
                                                        :confirmation {:title     (get-translation "requirement.confirm.delete.title"
                                                                                                   "Delete Requirement?")
                                                                       :body      (format-translation
                                                                                    (get-translation "entity.confirm.delete.body"
                                                                                                     "\"%{object-name}\" will be deleted permanently.")
                                                                                    :object-name (fu/truncate-text (:title row) 30))
                                                                       :yes-label (get-translation "generic.button.delete"
                                                                                                   "Delete")
                                                                       :no-label  (get-translation "generic.button.cancel"
                                                                                                   "Cancel")}}

                                                       {:label    (get-translation "generic.button.print" "Print")
                                                        :icon     icons/printer
                                                        :disabled? false
                                                        :on-click (fn []
                                                                    (mixpanel/track "Grid Actions" {"Action" "Print Requirement"})
                                                                    (batch-open-in-new-tab! (broutes/get-url-view "requirements#print") #{(:id row)} nil))}

                                                       {:label    (get-translation "entity.button.create-testset" "New Test Set")
                                                        :icon     icons/mux-testsets-runs
                                                        :disabled? (not has-edit-permissions)  
                                                        :on-click (fn []
                                                                    (mixpanel/track "Grid Actions" {"Action" "New Test Set"})
                                                                    (batch-navigate! (broutes/get-url-view "common#create_testset") #{(:id row)} nil {:entity "Requirement"}))}]])}]

                    :loading?              @(re-frame/subscribe [::subs/progress? ::common-events/fetch-entity-list])
                    :grid-state            grid-state  ; TODO: Check if can be removed because :local-state-updating
                    :local-state-updating  grid-state
                    :toggle-selection      (partial-dispatch [::grid.events/toggle-selection [current-entity state-key]])
                    :toggle-selection-page (partial-dispatch [::grid.events/toggle-selection-page [current-entity state-key]])
                    :on-query-changed      (partial-dispatch [::common-events/fetch-entity-list current-entity state-key])
                      ;; For filters
                    :get-field-props       (partial-subscribe [::forms/field-props :test])
                    :grid-toolbar          grid-buttons
                    :on-expand-click       (partial-dispatch [::common-events/toggle-expanded current-entity state-key])
                    :show-save-fast-filter? true}
        grid-props (merge (common-index-grid-props current-entity state-key table-id)
                          grid-props)]
    [:div.entities-grid {:class (when (:expanded? grid-props) "col")}
      [import-ticket/import-bugtracker-tickets-modal nil
       (import-ticket/import-bugtracker-tickets-modal-options {:current-entity :requirement
                                                               :grid-state grid-state})]
     [grid2/grid grid-props]]))

(defn index []
  [index-content-panel {:module "Requirement" :current-entity current-entity :state-key state-key :entities-grid entities-grid}])

(defn ^:export init [options]
  (let [{:keys [translation]} (-> options (js->clj :keywordize-keys true))]
    (i18n/append-translation! @(re-frame/subscribe [::subs/current-locale]) translation)))